import React, { useState, useEffect, useMemo } from "react"
import config from '../config.js';
import { apiErrorMessage } from '../utils/apiError';
import { desktopUi } from '../features/desktopUi';
import { loadVendors as fetchVendors, filterAndSortVendors, saveVendor } from '../features/vendors/vendorData';

const API_BASE = config.API_BASE_URL;

export default function VendorsPage() {
  const [vendors, setVendors] = useState([])
  const [selectedVendor, setSelectedVendor] = useState(null)

  // Left-panel list controls
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("name_asc")

  // Form fields (mirroring "Customers")
  const [name, setName] = useState("")
  const [contactName, setContactName] = useState("")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [address, setAddress] = useState("")
  const [city, setCity] = useState("")
  const [stateVal, setStateVal] = useState("")
  const [zipCode, setZipCode] = useState("")
  const [notes, setNotes] = useState("")
  const [isService, setIsService] = useState(false)
  const [serviceTypes, setServiceTypes] = useState("")

  // For error handling & loading
  const [error, setError] = useState(null)
  // Starts true: the mount effect always fetches, and a false start renders the
  // empty-list message for one frame before loadVendors flips it.
  const [loading, setLoading] = useState(true)

  // Fetch vendors on mount
  useEffect(() => {
    loadVendors()
  }, [])

  async function loadVendors() {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchVendors() // features/vendors/vendorData
      setVendors(data) // expect an array of vendor objects
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Mirrors the Parts and Assemblies list panels. "Created" sorts on vendor_id
  // because GET /vendors returns no created_at -- the id is monotonic, so it is
  // the same ordering the API's own default (ORDER BY vendor_id) produced.
  const filteredAndSortedVendors = useMemo(
    () => filterAndSortVendors(vendors, { searchTerm, sortBy }), // features/vendors/vendorData
    [vendors, searchTerm, sortBy]
  )

  function resetForm() {
    setName("")
    setContactName("")
    setPhone("")
    setEmail("")
    setAddress("")
    setCity("")
    setStateVal("")
    setZipCode("")
    setNotes("")
    setIsService(false)
    setServiceTypes("")
    setSelectedVendor(null)
  }

  function handleSelectVendor(v) {
    setSelectedVendor(v)
    setName(v.name || "")
    setContactName(v.contact_name || "")
    setPhone(v.phone || "")
    setEmail(v.email || "")
    setAddress(v.address || "")
    setCity(v.city || "")
    setStateVal(v.state || "")
    setZipCode(v.zip_code || "")
    setNotes(v.notes || "")
    setIsService(!!v.is_service)
    setServiceTypes(v.service_types || "")
  }

  async function handleSave() {
    // features/vendors/vendorData (the phone's save too): the name check, the body, POST / PATCH, the alert.
    const r = await saveVendor({
      vendorId: selectedVendor ? selectedVendor.vendor_id : null,
      form: {
        name, contact_name: contactName, phone, email, address, city, state: stateVal, zip_code: zipCode,
        notes, is_service: isService, service_types: serviceTypes,
      },
      ui: desktopUi,
      setBusy: (busy) => { setLoading(busy); if (busy) setError(null) },
    })
    if (r.error) setError(r.error)
    if (r.done) {
      loadVendors()
      resetForm()
    }
  }

  // Optional: you could add a delete function or an “inactive” toggle.
  // For example:
  async function handleDeleteVendor(v) {
    if (!window.confirm(`Are you sure you want to delete vendor #${v.vendor_id}?`)) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE}/vendors/${v.vendor_id}`, {
        method: "DELETE"
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      alert(`Vendor ${v.vendor_id} deleted.`)
      loadVendors()
      if (selectedVendor && selectedVendor.vendor_id === v.vendor_id) {
        resetForm()
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      {/* LEFT PANEL: vendor list */}
      <div className="border-end p-3" style={{ width: "300px", flexShrink: 0, overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Vendors</h5>
          <button className="btn btn-sm btn-outline-primary" onClick={resetForm}>New</button>
        </div>
        <div className="mb-2">
          <label className="form-label" htmlFor="vendorSortBy">Sort By</label>
          <select id="vendorSortBy" className="form-select form-select-sm" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
            <option value="date_newest">Newest Created</option>
            <option value="date_oldest">Oldest Created</option>
          </select>
        </div>

        <input
          type="text"
          className="form-control form-control-sm mb-2"
          placeholder="Search by name, contact, or ID..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />

        {loading && <div>Loading...</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        {!loading && !error && filteredAndSortedVendors.length === 0 && (
          <div className="text-muted small">
            {vendors.length === 0 ? "No vendors yet." : `No vendors match "${searchTerm}".`}
          </div>
        )}
        {!loading && filteredAndSortedVendors.map(v => (
          <div
            key={v.vendor_id}
            className={`p-2 border-bottom ${selectedVendor && selectedVendor.vendor_id === v.vendor_id ? "bg-primary-subtle" : "bg-white"}`}
            style={{ cursor: "pointer" }}
            onClick={() => handleSelectVendor(v)}
          >
            <div className="fw-bold">{v.name}{v.is_service && <span className="badge bg-info ms-1">Service</span>}</div>
            <div className="text-muted" style={{ fontSize: "0.9rem" }}>
              ID: {v.vendor_id}
            </div>
          </div>
        ))}
      </div>

      {/* RIGHT PANEL: vendor detail form */}
      <div className="flex-grow-1 p-4 overflow-auto">
        <h4>{selectedVendor ? `Editing Vendor #${selectedVendor.vendor_id}` : "Create New Vendor"}</h4>
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Contact Name</label>
            <input
              type="text"
              className="form-control"
              value={contactName}
              onChange={e => setContactName(e.target.value)}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">Phone</label>
            <input
              type="text"
              className="form-control"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Address</label>
          <input
            type="text"
            className="form-control"
            value={address}
            onChange={e => setAddress(e.target.value)}
          />
        </div>

        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">City</label>
            <input
              type="text"
              className="form-control"
              value={city}
              onChange={e => setCity(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">State</label>
            <input
              type="text"
              className="form-control"
              value={stateVal}
              onChange={e => setStateVal(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Zip Code</label>
            <input
              type="text"
              className="form-control"
              value={zipCode}
              onChange={e => setZipCode(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Notes</label>
          <textarea
            className="form-control"
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        <div className="mb-3 p-3 border rounded bg-light">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="vendorIsService"
              checked={isService}
              onChange={e => setIsService(e.target.checked)}
            />
            <label className="form-check-label fw-bold" htmlFor="vendorIsService">
              Service vendor (coater / plater / finisher)
            </label>
          </div>
          <div className="form-text mb-2">
            Service vendors appear in the Coating-vendor picker on parts and on outside-processing orders.
          </div>
          {isService && (
            <div>
              <label className="form-label small">Service types (optional)</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Powder Coat, Anodize, Zinc Plate"
                value={serviceTypes}
                onChange={e => setServiceTypes(e.target.value)}
              />
            </div>
          )}
        </div>

        <button className="btn btn-primary me-2" onClick={handleSave} disabled={loading}>
          {selectedVendor ? "Save Changes" : "Create Vendor"}
        </button>

        {selectedVendor && (
          <button
            className="btn btn-danger"
            onClick={() => handleDeleteVendor(selectedVendor)}
            disabled={loading}
          >
            Delete Vendor
          </button>
        )}
      </div>
    </div>
  )
}
